import { useEffect, useState } from 'react';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { cn, ImagesToImageMap, useClientTranslation } from '@/lib/core';
import { useApp } from '@/lib/core/providers/appProvider';
import type { FavoriteListDto } from '@/services/platform/api/model';

// ── Types ─────────────────────────────────────────────────────────────────────

export type FavoritesTab = 'Shop' | 'Events';

export interface ProfileFavoritesProps {
  activeTab: FavoritesTab;
  onTabChange: (tab: FavoritesTab) => void;
  /** Lists already filtered to the active tab's ListType. */
  lists: FavoriteListDto[];
  isLoading?: boolean;
  onBack: () => void;
  onCreateList: () => void;
  onOpenList: (publicGuid: string) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ProfileFavorites({
  activeTab,
  onTabChange,
  lists,
  isLoading = false,
  onCreateList,
  onOpenList,
}: ProfileFavoritesProps) {
  const t = useClientTranslation();
  const { appSettings } = useApp();
  const eventFallbackImageUrl = ImagesToImageMap(appSettings?.properties?.eventNoPicImage)?.src;

  const tabs: { id: FavoritesTab; label: string }[] = [
    { id: 'Shop', label: t('App.Shop.Favorites.ShopTab') },
    { id: 'Events', label: t('App.Shop.Favorites.EventsTab') },
  ];

  const emptyTitle =
    activeTab === 'Shop'
      ? t('App.Shop.Favorites.EmptyStateTitleShop')
      : t('App.Shop.Favorites.EmptyStateTitleEvents');
  const emptyDescription =
    activeTab === 'Shop'
      ? t('App.Shop.Favorites.EmptyStateDescriptionShop')
      : t('App.Shop.Favorites.EmptyStateDescriptionEvents');

  const hasLists = !isLoading && lists.length > 0;

  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          'flex flex-1 flex-col overflow-y-auto px-4 pt-6 pb-24',
          hasLists ? 'gap-6' : 'gap-[120px]'
        )}
      >
        {/* Shop / Events tab switcher */}
        <div className="flex w-full items-center gap-4">
          {tabs.map(tab => {
            const isActive = tab.id === activeTab;
            return (
              <Button
                key={tab.id}
                variant="tertiary-link"
                palette="dark"
                onClick={() => onTabChange(tab.id)}
                className={cn(
                  'h-auto min-w-9 flex-col gap-0.5 rounded-none px-0 py-0 text-[14px] tracking-[0.5px]',
                  isActive && 'font-semibold'
                )}
              >
                <span>{tab.label}</span>
                <span className={cn('h-px w-4', isActive ? 'bg-jncquoi-neutral-1000' : 'bg-transparent')} />
              </Button>
            );
          })}
        </div>

        {/* Empty state — no lists created yet for this tab */}
        {!isLoading && lists.length === 0 && (
          <div className="flex flex-col items-center gap-2">
            <div className="flex size-11 shrink-0 items-center justify-center">
              <Icon name="heart" width={20} height={20} className="text-jncquoi-neutral-1000" />
            </div>
            <div className="flex flex-col gap-2 text-center tracking-[0.5px]">
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] uppercase">
                {emptyTitle}
              </p>
              <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[14px]">{emptyDescription}</p>
            </div>
          </div>
        )}

        {/* List rows — tap to open the list detail */}
        {hasLists && (
          <div className="flex w-full flex-col">
            {lists.map(list => (
              <button
                key={list.publicGuid}
                type="button"
                className="border-jncquoi-neutral-200 flex w-full flex-col gap-2 border-b-[0.5px] py-4 text-left"
                onClick={() => list.publicGuid && onOpenList(list.publicGuid)}
              >
                <span className="flex items-center justify-between">
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[16px] font-semibold tracking-[0.25px]">
                    {list.name}
                  </span>
                  <Icon name="nav-arrow-right" width={16} height={16} className="text-jncquoi-neutral-1000" />
                </span>
                <span className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[12px] font-medium tracking-[0.5px]">
                  {activeTab === 'Shop'
                    ? t('App.Shop.Favorites.SavedItemsCount', { count: list.itemCount })
                    : t('App.Shop.Favorites.SavedEventsCount', { count: list.itemCount })}
                </span>
                {list.previewImageUrls && list.previewImageUrls.length > 0 && (
                  <span className="flex items-center gap-2">
                    {list.previewImageUrls.map((url, index) => (
                      <ListPreviewImage
                        key={index}
                        url={url}
                        fallbackUrl={activeTab === 'Events' ? eventFallbackImageUrl : undefined}
                      />
                    ))}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="bg-jncquoi-neutral-00 fixed inset-x-0 bottom-0 px-4 pb-12">
        <Button variant="primary-solid" size="lg" onClick={onCreateList} className="w-full">
          {t('App.Shop.Favorites.CreateListButton')}
        </Button>
      </div>
    </div>
  );
}

// ── Preview thumbnail ────────────────────────────────────────────────────────

/** Falls back to `fallbackUrl` (e.g. the CMS no-pic image on the Events tab) when `url` is missing or fails to load. */
function ListPreviewImage({ url, fallbackUrl }: { url?: string | null; fallbackUrl?: string }) {
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [url]);
  const showImage = !!url && !imageFailed;

  return (
    <span className="bg-jncquoi-neutral-50 relative size-16 shrink-0 overflow-hidden">
      {showImage ? (
        <img
          src={url}
          alt=""
          className="absolute inset-0 size-full object-cover"
          loading="lazy"
          onError={() => setImageFailed(true)}
        />
      ) : fallbackUrl ? (
        <img src={fallbackUrl} alt="" className="absolute inset-0 size-full object-cover" loading="lazy" />
      ) : null}
    </span>
  );
}
